import Phaser from 'phaser';
import { Player } from '../entities/Player';
import { Enemy } from '../entities/Enemy';
import { GameConfig } from '../config/GameConfig';

export class MilkPuddle extends Phaser.GameObjects.Graphics {
  public damage: number;
  private enemies: Phaser.Physics.Arcade.Group;
  private puddleScene: Phaser.Scene;
  private radius: number = GameConfig.WEAPON_DATA.MILK_SPLASH.RADIUS;
  private damageTimer?: Phaser.Time.TimerEvent;
  private lifetime: number = GameConfig.WEAPON_DATA.MILK_SPLASH.LIFESPAN;

  constructor(scene: Phaser.Scene, x: number, y: number, enemies: Phaser.Physics.Arcade.Group, damage: number) {
    super(scene);

    this.damage = damage;
    this.enemies = enemies;
    this.puddleScene = scene;

    scene.add.existing(this);
    this.setPosition(x, y);
    this.setDepth(2);

    this.draw();
    this.startDamaging();

    // Fade out and destroy
    this.puddleScene.time.delayedCall(this.lifetime, () => {
      this.puddleScene.tweens.add({
        targets: this,
        alpha: 0,
        duration: 500,
        onComplete: () => {
          this.destroy();
        }
      });
    });
  }

  private draw(): void {
    // Milk puddle appearance
    this.fillStyle(0xFFFFFF, 0.6);
    this.fillEllipse(0, 0, this.radius, this.radius * 0.8);

    // Lighter center
    this.fillStyle(0xFFFFFF, 0.8);
    this.fillEllipse(0, 0, this.radius * 0.6, this.radius * 0.5);

    // Shine effect
    this.fillStyle(0xFFFFFF, 0.3);
    this.fillEllipse(-this.radius * 0.2, -this.radius * 0.1, this.radius * 0.3, this.radius * 0.2);
  }

  private startDamaging(): void {
    this.damageTimer = this.puddleScene.time.addEvent({
      delay: GameConfig.WEAPON_DATA.MILK_SPLASH.TICK_RATE, // Damage twice per second
      callback: () => {
        if (!this.active) return;

        const enemyArray = this.enemies.getChildren() as Enemy[];
        enemyArray.forEach(enemy => {
          if (!enemy.active) return;

          const distance = Phaser.Math.Distance.Between(
            this.x, this.y,
            enemy.x, enemy.y
          );

          if (distance < this.radius * 0.5) {
            enemy.takeDamage(this.damage);
          }
        });
      },
      callbackScope: this,
      loop: true
    });
  }

  destroy(): void {
    if (this.damageTimer) {
      this.damageTimer.destroy();
    }
    super.destroy();
  }
}

export class MilkSplashWeapon {
  private scene: Phaser.Scene;
  private player: Player;
  private enemies: Phaser.Physics.Arcade.Group;
  private attackTimer?: Phaser.Time.TimerEvent;
  private damageMultiplier: number = 1;
  private attackDelay: number = GameConfig.WEAPON_DATA.MILK_SPLASH.COOLDOWN;
  private puddleCount: number = 1;

  constructor(scene: Phaser.Scene, player: Player, enemies: Phaser.Physics.Arcade.Group) {
    this.scene = scene;
    this.player = player;
    this.enemies = enemies;

    this.startAutoAttack();
  }

  private startAutoAttack(): void {
    this.attackTimer = this.scene.time.addEvent({
      delay: this.attackDelay,
      callback: this.attack,
      callbackScope: this,
      loop: true
    });
  }

  private attack(): void {
    if (!this.player.active) return;

    // Spawn multiple puddles around the player
    for (let i = 0; i < this.puddleCount; i++) {
      const angle = (i / this.puddleCount) * Math.PI * 2;
      const distance = 80;
      const x = this.player.x + Math.cos(angle) * distance;
      const y = this.player.y + Math.sin(angle) * distance;

      new MilkPuddle(this.scene, x, y, this.enemies, GameConfig.WEAPON_DATA.MILK_SPLASH.DAMAGE * this.damageMultiplier);
    }
  }

  public updateDamageMultiplier(multiplier: number): void {
    this.damageMultiplier = multiplier;
  }

  public upgradeDamage(amount: number): void {
    this.damageMultiplier += amount;
  }

  public upgradeSpeed(): void {
    this.attackDelay = Math.max(1500, this.attackDelay * GameConfig.WEAPON_DATA.MILK_SPLASH.COOLDOWN_UPGRADE);
    if (this.attackTimer) {
      this.attackTimer.destroy();
    }
    this.startAutoAttack();
  }

  public addPuddle(): void {
    this.puddleCount++;
  }

  public destroy(): void {
    if (this.attackTimer) {
      this.attackTimer.destroy();
    }
  }
}
